import React, { useRef } from 'react';
import { Navigate } from 'react-router';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { Resource } from 'react-admin';
import EnhancedClaimsList from '../claims-report/report';
import Fees from '../msa-fees/fees';
import Reports from '../reports/reports';
import HistoricalReports from '../historical-reports/HistoricalReports';
import BillingClients from '../billing-clients/BillingClients';
import MemberSummary from '../member-summary/MemberSummary';
import ClaimExceptions from '../claim-exceptions/ClaimExceptions';
import ReconSemiMonthly from '../recon-semimonthly/ReconSemiMonthly';
import Crosswalks from '../crosswalks/Crosswalks';
import InvoiceDetailException from '../invoice-detail-exception/InvoiceDetailException';
import ManageClientSplit from '../manage-client-split/ManageClientSplit';
import { MemberSummaryRef } from '../utils/DataTypes';
import MedicaidClaimUpload from '../medicaidUpload/MedicaidClaimUpload';
import ClientSplitRulesList from '../manage-client-split/ClientSplitRulesList';
import ExceptionConfigPage from '../exception-config/ExceptionConfigPage';
import BillingClientsFtps from '../billing-clients-ftps/BillingClientsFtps';
import { BillingOperations } from '../billing-operations/BillingOperations';
import BillingGuidelines from '../billing-guidelines/BillingGuidelines';

const isProd = window.location.hostname === 'billingmart.myplantruerx.com';
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  memberSummaryRef?: React.Ref<MemberSummaryRef>;
}
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, memberSummaryRef, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && value == 0 && <Resource name="claimsreport" list={EnhancedClaimsList} {...props} />}
      {value === index && value == 4 && <Resource name="fees" list={Fees} {...props} />}
      {value === index && value == 5 && <Reports />}
      {value === index && value == 6 && <HistoricalReports />}
      {value === index && value == 7 && <MedicaidClaimUpload />}
      {value === index && value == 8 && <BillingClients />}
      {value === index && value == 9 && <MemberSummary ref={memberSummaryRef} isActive={value === 9} />}
      {value === index && value == 10 && <ClaimExceptions />}
      {/* {value === index && value == 11 && <ReconSemiMonthly />} */}
      {value === index && value == 11 && <Crosswalks />}
      {value === index && value == 12 && <ExceptionConfigPage />}
      {value === index && value == 13 && <ManageClientSplit />}
      {value === index && value == 14 && <ClientSplitRulesList />}
      {value === index && value == 15 && <BillingClientsFtps />}
      {value === index && value == 16 && <BillingOperations />}
      {value === index && value == 17 && <BillingGuidelines />}
      {value === index &&
        value != 0 &&
        value != 4 &&
        value != 5 &&
        value != 6 &&
        value != 7 &&
        value != 8 &&
        value != 9 &&
        value != 10 &&
        value != 11 &&
        value != 12 &&
        value != 13 &&
        value != 14 &&
        value != 15 &&
        value != 16 && 
        value != 17 && (
          <Box flex="1">
            <Typography variant="h5" component="h2" gutterBottom>
              Coming Soon.....
            </Typography>
          </Box>
        )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
const tabs = [
  // { id: 0, name: 'claims' },
  // { id: 1, name: 'Invoice Data' },
  // { id: 2, name: 'Account Billing' },
  // { id: 3, name: 'Prior Authorizations' },
  // { id: 4, name: 'Administration' },
  { id: 5, name: 'Generate Reports' },
  { id: 6, name: 'Report Status' },
  ...(!isProd
    ? [
        { id: 7, name: 'Medicaid Subrogation' },
        // { id: 8, name: 'Billing Clients' },
        { id: 9, name: 'Create Member Summaries' },
        { id: 10, name: 'Claim Exceptions' },
        // { id: 11, name: 'Semi-Monthly Recon' },
        { id: 11, name: 'Manage Crosswalks' },
        { id: 12, name: 'Report Configurations' },
        { id: 13, name: 'Create Client Splits' },
        { id: 14, name: 'Review Split Rules' },
        { id: 15, name: 'FTP Folders' },
        { id: 16, name: 'Billing Operations' },
        { id: 17, name: 'Billing Guidelines' },
      ]
    : []),
];
const Dashboard = () => {
  //return <Navigate to={'/billingreports'} />
  const [value, setValue] = React.useState(5);

  const memberSummaryRef = useRef<MemberSummaryRef>(null);

  const handleChange = async (event: React.SyntheticEvent, newValue: number) => {
    const isMemberSummaryTab = value === 8;
    const leavingMemberSummary = isMemberSummaryTab && newValue !== 8;

    const hasUnsaved = memberSummaryRef.current?.hasUnsavedChanges;

    if (hasUnsaved && leavingMemberSummary) {
      const confirmLeave = await memberSummaryRef.current.getUnsavedChangesConfirmation();

      if (!confirmLeave) {
        return;
      }
    }

    setValue(newValue);
  };

  return (
    <>
      <Divider sx={{ height: '40px', border: 0 }} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          TabIndicatorProps={{
            style: {
              backgroundColor: 'rgba(210, 100, 80, 1)',
              height: '2px',
            },
          }}
          indicatorColor="primary"
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          {tabs.map((choice) => (
            <Tab
              sx={{ justifyContent: 'left', textTransform: 'capitalize', fontWeight: 'bolder !important' }}
              key={choice.id}
              {...a11yProps(choice.id)}
              label={<span>{choice.name}</span>}
              value={choice.id}
            />
          ))}
        </Tabs>
      </Box>
      {/* <CustomTabPanel value={value} index={0}></CustomTabPanel> */}
      {/* <CustomTabPanel value={value} index={1}></CustomTabPanel>
      <CustomTabPanel value={value} index={2}></CustomTabPanel>
      <CustomTabPanel value={value} index={3}></CustomTabPanel> */}
      {/* <CustomTabPanel value={value} index={4}></CustomTabPanel> */}
      <CustomTabPanel value={value} index={5}></CustomTabPanel>
      <CustomTabPanel value={value} index={6}></CustomTabPanel>
      {!isProd && <CustomTabPanel value={value} index={7}></CustomTabPanel>}
      {!isProd && <CustomTabPanel value={value} index={8} memberSummaryRef={memberSummaryRef}></CustomTabPanel>}
      {!isProd && <CustomTabPanel value={value} index={9}></CustomTabPanel>}
      {!isProd && <CustomTabPanel value={value} index={10}></CustomTabPanel>}
      <CustomTabPanel value={value} index={11}></CustomTabPanel>
      {!isProd && <CustomTabPanel value={value} index={12}></CustomTabPanel>}
      {!isProd && <CustomTabPanel value={value} index={13}></CustomTabPanel>}
      {!isProd && <CustomTabPanel value={value} index={14}></CustomTabPanel>}
      <CustomTabPanel value={value} index={15}></CustomTabPanel>
      {!isProd && <CustomTabPanel value={value} index={16}></CustomTabPanel>}
      {!isProd && <CustomTabPanel value={value} index={17}></CustomTabPanel>}
    </>
  );
};

export default Dashboard;
